import type { AxiosInstance } from 'axios';
import { AgentResourcesClient } from './agent-resources.js';
import { AppClient } from './app.js';
import { AudioStatusClient } from './audio-status.js';
import { ChatContextClient } from './chat-context.js';
import { ChatInstanceClient } from './chat-instances.js';
import { ChatReportsClient } from './chat-reports.js';
import { ChatChartPreferencesClient } from './chat.charts.js';
import { ChatBaseClient } from './chat.js';
import { FormEnhanceClient } from './form-enhance.js';
import { FormFileUploadClient } from './form-file-upload.js';
import { GroupsClient } from './groups.js';
import { HcpClient } from './hcp.js';
import { KolSearchClient } from './kol.js';
import { LocationClient } from './location.js';
import { MessageGenerationClient } from './message-generation.js';
import { MessageTestingClient } from './message-testing.js';
import { PagesClient } from './pages.js';
import { ProjectsEntitiesClient } from './projects.entities.js';
import { ProjectsClient } from './projects.js';
import { ProjectQuantAnalysesClient } from './projects.quant-analysis.js';
import { ProjectsTranscriptsClient } from './projects.transcripts.js';
import { ProxiesClient } from './proxies.js';
import { QueriesClient } from './queries.js';
import { SearchClient } from './search.js';
import { SegmentsClient } from './segments.js';
import { SmartRoutingClient } from './smart-routing.js';
import { SpeechClient } from './speech.js';
import { SurveysClient } from './surveys.js';
import { TagsClient } from './tags.js';
import { TextToSpeechClient } from './text-to-speech.js';
import { TranscriptsClient } from './transcripts.js';
import { TranscriptsSummaryClient } from './transcripts.summary.js';
import { UsersClient } from './users.js';
import { WorkspaceBrandsClient } from './workspaces.brand.js';
import { WorkspacesFileUploadClient } from './workspaces.file-upload.js';
import WorkspacesFileApiClient from './workspaces.file.js';
import { WorkspacesFolderClient } from './workspaces.folder.js';
import { WorkspacesGlobalSearchClient } from './workspaces.global-search.js';
import { WorkspacesClient } from './workspaces.js';
import { WorkspacesObjectClient } from './workspaces.object.js';
import { WorkspacesTranscriptClient } from './workspaces.transcript.js';

type BuildEnhancedChatApiClient = {
  authenticatedClient: AxiosInstance;
  unauthenticatedClient: AxiosInstance;
};

export function buildEnhancedChatApiClient({
  authenticatedClient,
  unauthenticatedClient,
}: BuildEnhancedChatApiClient) {
  return {
    app: new AppClient(authenticatedClient, unauthenticatedClient),
    audioStatus: new AudioStatusClient(authenticatedClient),
    agentResources: new AgentResourcesClient(authenticatedClient),
    chat: new ChatBaseClient(authenticatedClient),
    chatInstances: new ChatInstanceClient(authenticatedClient),
    chatCharts: new ChatChartPreferencesClient(authenticatedClient),
    chatContext: new ChatContextClient(authenticatedClient),
    chatReports: new ChatReportsClient(authenticatedClient),
    groups: new GroupsClient(authenticatedClient),
    hcp: new HcpClient(authenticatedClient),
    kolSearch: new KolSearchClient(authenticatedClient),
    location: new LocationClient(authenticatedClient),
    pages: new PagesClient(authenticatedClient),
    projects: new ProjectsClient(authenticatedClient),
    projectsEntities: new ProjectsEntitiesClient(authenticatedClient),
    projectsTranscripts: new ProjectsTranscriptsClient(authenticatedClient),
    proxies: new ProxiesClient(authenticatedClient),
    quantAnalysis: new ProjectQuantAnalysesClient(authenticatedClient),
    queries: new QueriesClient(authenticatedClient),
    search: new SearchClient(authenticatedClient),
    smartRouting: new SmartRoutingClient(authenticatedClient),
    speech: new SpeechClient(authenticatedClient),
    tags: new TagsClient(authenticatedClient),
    textToSpeech: new TextToSpeechClient(authenticatedClient),
    transcripts: new TranscriptsClient(authenticatedClient),
    workspaces: new WorkspacesClient(authenticatedClient),
    workspaceBrands: new WorkspaceBrandsClient(authenticatedClient),
    workspacesFile: new WorkspacesFileApiClient(authenticatedClient),
    workspacesFileUpload: new WorkspacesFileUploadClient(authenticatedClient),
    workspacesFolder: new WorkspacesFolderClient(authenticatedClient),
    workspacesObject: new WorkspacesObjectClient(authenticatedClient),
    workspacesGlobalSearch: new WorkspacesGlobalSearchClient(authenticatedClient),
    workspacesTranscript: new WorkspacesTranscriptClient(authenticatedClient),
    transcriptsSummary: new TranscriptsSummaryClient(authenticatedClient),
    users: new UsersClient(authenticatedClient),
    formEnhance: new FormEnhanceClient(authenticatedClient),
    formFileUpload: new FormFileUploadClient(authenticatedClient),
    messageGeneration: new MessageGenerationClient(authenticatedClient),
    messageTesting: new MessageTestingClient(authenticatedClient),
    surveys: new SurveysClient(authenticatedClient),
    segments: new SegmentsClient(authenticatedClient),

    authenticatedClient,
    unauthenticatedClient,
  };
}

export type ChatAppApiClient = ReturnType<typeof buildEnhancedChatApiClient>;
